import React from "react";

// ── Discord-style Markdown Renderer ─────────────────────────────
// Supports: # ## ### headings, **bold**, *italic*, __underline__,
//           ~~strike~~, `code`, ```code block```, > blockquote,
//           - list items, newlines, and :emoji: shortcodes.

// ── Emoji shortcode map ──────────────────────────────────────────
const EMOJI_MAP = {
  // Celebrations & awards
  trophy: "🏆", first_place: "🥇", second_place: "🥈", third_place: "🥉",
  tada: "🎉", confetti_ball: "🎊", partying_face: "🥳", gift: "🎁",
  medal: "🎖️", ribbon: "🎀", star: "⭐", star2: "🌟", sparkles: "✨",
  fire: "🔥", boom: "💥", zap: "⚡", crown: "👑",

  // People & gestures
  wave: "👋", raised_hands: "🙌", clap: "👏", pray: "🙏",
  thumbsup: "+1", thumbsdown: "-1", ok_hand: "👌", muscle: "💪",
  handshake: "🤝", saluting_face: "🫡", heart_hands: "🫶",

  // Faces & emotions
  smile: "😄", grinning: "😀", joy: "😂", rofl: "🤣", sweat_smile: "😅",
  wink: "😉", blush: "😊", heart_eyes: "😍", sunglasses: "😎",
  thinking: "🤔", monocle_face: "🧐", nerd_face: "🤓",
  scream: "😱", sob: "😭", cry: "😢", pleading_face: "🥺",
  angry: "😠", rage: "😡", skull: "💀", dizzy_face: "😵",
  shushing_face: "🤫", salute: "🫡", zombie: "🧟",

  // Hearts & symbols
  heart: "❤️", orange_heart: "🧡", yellow_heart: "💛", green_heart: "💚",
  blue_heart: "💙", purple_heart: "💜", black_heart: "🖤", white_heart: "🤍",
  broken_heart: "💔", revolving_hearts: "💞", sparkling_heart: "💖",
  x: "❌", check: "✅", white_check_mark: "✅", warning: "⚠️",
  no_entry: "⛔", stop_sign: "🛑", exclamation: "❗", question: "❓",
  information_source: "ℹ️", sos: "🆘", new: "🆕", up: "🆙", cool: "🆒",
  free: "🆓",

  // Gaming & Minecraft
  crossed_swords: "⚔️", shield: "🛡️", bow_and_arrow: "🏹", dagger: "🗡️",
  axe: "🪓", pick: "⛏️", hammer: "🔨", wrench: "🔧", chains: "⛓️",
  gem: "💎", diamond: "🔷", crystal_ball: "🔮", magic_wand: "🪄",
  dragon: "🐉", dragon_face: "🐲", zombie_face: "🧟", skull_crossbones: "☠️",
  spider: "🕷️", spider_web: "🕸️", bat: "🦇", wolf: "🐺",
  chest: "🎁", map: "🗺️", compass: "🧭", scroll: "📜",
  potion: "🧪", test_tube: "🧫", alembic: "⚗️",
  arrow_up: "⬆️", arrow_down: "⬇️", arrow_left: "⬅️", arrow_right: "➡️",
  level_up: "⬆️", level_slider: "🎚️",

  // Nature & environment
  sunny: "☀️", cloud: "☁️", rain: "🌧️", snow: "❄️", thunder: "⛈️",
  moon: "🌙", full_moon: "🌕", sunrise: "🌅", night: "🌃",
  mountain: "⛰️", volcano: "🌋", island: "🏝️", forest: "🌲",
  tree: "🌳", fallen_leaf: "🍂", mushroom: "🍄", four_leaf_clover: "🍀",

  // Objects & misc
  sword: "⚔️", lock: "🔒", unlock: "🔓", key: "🔑", bell: "🔔",
  mega: "📣", loudspeaker: "📢", envelope: "✉️", email: "📧",
  calendar: "📅", clock1: "🕐", alarm_clock: "⏰", hourglass: "⌛",
  timer: "⏱️", stopwatch: "⏱️",
  link: "🔗", paperclip: "📎", pushpin: "📌", bookmark: "🔖",
  label: "🏷️", ticket: "🎫", id: "🪪",
  package: "📦", inbox_tray: "📥", outbox_tray: "📤",
  bar_chart: "📊", chart_with_upwards_trend: "📈", chart_with_downwards_trend: "📉",
  pencil: "✏️", pen: "🖊️", notebook: "📓", books: "📚",
  loudspeaker2: "🔊", mute: "🔇", headphones: "🎧", microphone: "🎙️",
  video_game: "🎮", joystick: "🕹️", dice: "🎲",
  ferris_wheel: "🎡", roller_coaster: "🎢", carousel_horse: "🎠",
  fireworks: "🎆", sparkler: "🎇",

  // Status & progress
  white_circle: "⚪", black_circle: "⚫", red_circle: "🔴",
  blue_circle: "🔵", green_circle: "🟢", yellow_circle: "🟡",
  orange_circle: "🟠", purple_circle: "🟣", brown_circle: "🟤",
  small_red_triangle: "🔺", small_red_triangle_down: "🔻",
  radio_button: "🔘", record_button: "⏺️", play_button: "▶️",
  pause_button: "⏸️", stop_button: "⏹️",
};

/**
 * Replace :shortcode: patterns with their Unicode emoji equivalents.
 */
function replaceEmojis(text) {
  return text.replace(/:([a-z0-9_+\-]+):/g, (match, code) => {
    return EMOJI_MAP[code] ?? match; // keep original if not found
  });
}

/**
 * Parse inline markdown (bold, italic, underline, strike, code)
 * and return an array of React nodes.
 */
function parseInline(text, key = 0) {
  // Ordered by priority
  const patterns = [
    { re: /```([\s\S]+?)```/g,    tag: "code",   cls: "dmd-codeblock" },
    { re: /`([^`]+)`/g,           tag: "code",   cls: "dmd-inline-code" },
    { re: /\*\*(.+?)\*\*/g,       tag: "strong", cls: null },
    { re: /__(.+?)__/g,           tag: "u",      cls: null },
    { re: /~~(.+?)~~/g,           tag: "s",      cls: null },
    { re: /\*(.+?)\*/g,           tag: "em",     cls: null },
    { re: /_(.+?)_/g,             tag: "em",     cls: null },
  ];

  // We'll do a single-pass tokenizer
  const tokens = [];
  let remaining = text;
  let safetyKey = 0;

  while (remaining.length > 0) {
    safetyKey++;
    if (safetyKey > 2000) break; // safety guard

    let earliest = null;
    let earliestIndex = Infinity;

    for (const p of patterns) {
      p.re.lastIndex = 0;
      const m = p.re.exec(remaining);
      if (m && m.index < earliestIndex) {
        earliest = { match: m, tag: p.tag, cls: p.cls };
        earliestIndex = m.index;
      }
    }

    if (!earliest) {
      tokens.push(remaining);
      break;
    }

    const { match, tag, cls } = earliest;

    // Push text before match
    if (match.index > 0) {
      tokens.push(remaining.slice(0, match.index));
    }

    const inner = match[1];
    const El = tag;
    tokens.push(
      cls
        ? <El key={`${key}-${safetyKey}`} className={cls}>{inner}</El>
        : <El key={`${key}-${safetyKey}`}>{inner}</El>
    );

    remaining = remaining.slice(match.index + match[0].length);
  }

  return tokens;
}

/**
 * Split raw text into lines and render each as the appropriate
 * Discord-markdown element.
 */
function renderLines(text) {
  const lines = text.split("\n");
  const output = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // ── Fenced code block ────────────────────────────────────
    if (line.startsWith("```")) {
      const lang = line.slice(3).trim();
      const codeLines = [];
      i++;
      while (i < lines.length && !lines[i].startsWith("```")) {
        codeLines.push(lines[i]);
        i++;
      }
      output.push(
        <pre key={i} className="dmd-pre">
          {lang && <span className="dmd-lang">{lang}</span>}
          <code>{codeLines.join("\n")}</code>
        </pre>
      );
      i++; // skip closing ```
      continue;
    }

    // ── Headings ─────────────────────────────────────────────
    if (/^### (.+)/.test(line)) {
      output.push(<h3 key={i} className="dmd-h3">{parseInline(line.slice(4), i)}</h3>);
    } else if (/^## (.+)/.test(line)) {
      output.push(<h2 key={i} className="dmd-h2">{parseInline(line.slice(3), i)}</h2>);
    } else if (/^# (.+)/.test(line)) {
      output.push(<h1 key={i} className="dmd-h1">{parseInline(line.slice(2), i)}</h1>);

    // ── Blockquote ───────────────────────────────────────────
    } else if (line.startsWith("> ")) {
      output.push(
        <blockquote key={i} className="dmd-quote">
          {parseInline(line.slice(2), i)}
        </blockquote>
      );

    // ── Unordered list item ──────────────────────────────────
    } else if (/^[-*] (.+)/.test(line)) {
      output.push(
        <div key={i} className="dmd-li">
          <span className="dmd-li-dot">•</span>
          <span>{parseInline(line.slice(2), i)}</span>
        </div>
      );

    // ── Divider ──────────────────────────────────────────────
    } else if (/^---+$/.test(line.trim())) {
      output.push(<hr key={i} className="dmd-hr" />);

    // ── Empty line → small gap ───────────────────────────────
    } else if (line.trim() === "") {
      output.push(<div key={i} className="dmd-gap" />);

    // ── Regular paragraph line ───────────────────────────────
    } else {
      output.push(
        <p key={i} className="dmd-p">{parseInline(line, i)}</p>
      );
    }

    i++;
  }

  return output;
}

export default function DiscordMarkdown({ text, className = "" }) {
  if (!text) return null;
  // Pre-process emoji shortcodes on the whole text before line splitting
  const processed = replaceEmojis(text);
  return (
    <div className={`dmd-root ${className}`}>
      {renderLines(processed)}
    </div>
  );
}
